import { AvatarWithFallback } from "@/components/AvatarWithFallback"

interface DeveloperCardProps {
  id: number
  nickname: string | null
  avatar: string | null
  workYears: string | null
  successRate: number | null
  recentlyActiveString: string | null
  isJobSeeking: number | null
  freelanceStatus: number | null
  cityString: string | null
  skills: Array<{ skill_tag_string: string | null; is_main: number | null }>
  industryExperiences: Array<{ industry_tag_string: string | null }>
  careerTags: Array<{ career_tag1_string: string | null; career_tag2_string: string | null }>
}

export function DeveloperCard({
  id,
  nickname,
  avatar,
  workYears,
  successRate,
  recentlyActiveString,
  isJobSeeking,
  freelanceStatus,
  cityString,
  skills,
  industryExperiences,
  careerTags,
}: DeveloperCardProps) {
  const getJobStatusText = () => {
    if (freelanceStatus === 1) {
      if (isJobSeeking === 1) return "接单·求职中"
      if (isJobSeeking === 2) return "接单·考虑求职"
      if (isJobSeeking === 3) return "接单·不求职"
      return "接单"
    }
    return "不接单"
  }

  const mainSkills = skills.filter(s => s.is_main === 1).slice(0, 5)
  const allSkills = skills.slice(0, 8)
  const industries = industryExperiences.slice(0, 4).map(i => i.industry_tag_string).filter(Boolean)

  return (
    <a
      href={`/programmer/${id}`}
      className="group block bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
    >
      {/* Header: Avatar + Name + Rating */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
          <AvatarWithFallback src={avatar} name={nickname} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
            {nickname || "未设置昵称"}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            {workYears && (
              <span className="text-xs text-gray-600">{workYears}经验</span>
            )}
            {successRate !== null && successRate > 0 && (
              <span className="text-xs text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">
                综合评分 {successRate}分
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Job Status */}
      <div className="mb-2">
        <div className="text-xs text-gray-500">
          求职意愿：{getJobStatusText()}
          {recentlyActiveString && (
            <span className="ml-1">({recentlyActiveString}更新)</span>
          )}
        </div>
      </div>

      {/* Location + Active Status */}
      <div className="flex items-center gap-2 text-xs text-gray-600 mb-3">
        {cityString && <span>{cityString}</span>}
        {recentlyActiveString && (
          <>
            <span>•</span>
            <span>{recentlyActiveString}</span>
          </>
        )}
      </div>

      {/* Skills */}
      {allSkills.length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">技能：</div>
          <div className="flex flex-wrap gap-1">
            {allSkills.map((skill, idx) => (
              <span
                key={idx}
                className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded"
              >
                {skill.skill_tag_string}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Industries */}
      {industries.length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">行业：</div>
          <div className="flex flex-wrap gap-1">
            {industries.map((industry, idx) => (
              <span
                key={idx}
                className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded"
              >
                {industry}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Career Direction */}
      {careerTags.length > 0 && careerTags[0]?.career_tag2_string && (
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">方向：</div>
          <div className="flex flex-wrap gap-1">
            {careerTags.map((tag, idx) => (
              <span
                key={idx}
                className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded"
              >
                {tag.career_tag1_string}-{tag.career_tag2_string}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Hourly Rate Badge (if applicable) */}
      {successRate !== null && successRate >= 40 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs text-orange-600 font-medium">
            1000元/8h
          </span>
        </div>
      )}
    </a>
  )
}
